import classNames from 'classnames'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Form } from '@lib/formik'
import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'
import { usePathHistoryContext } from '@context/PathHistoryContext'

import { ExchangeRateBox } from '@ui/ExchangeRateBox/ExchangeRateBox'
import { Button } from '@ui/components/Button'

import { PageData, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { WalletRestrictions, isRestricted } from '@utils/wallet.utils'

import { NameDto } from '@model/NameDto'
import {
  BusinessRulesEnum,
  CampaignBusinessRuleDto,
  TradingAccount,
  TradingAccountCampaign,
} from '@model/TradingAccount'
import { CurrencyType, WalletDto, WalletTypeEnum } from '@model/WalletDto'

import { PercentageAmountChip } from '@global/PercentageChip/PercentageAmountChip'
import { createFormField } from '@global/formField/FormField'
import { createFormNumericField } from '@global/formField/FormNumericField'
import { FormTemplate } from '@global/templates/FormTemplate'

import { useFormatNumber } from '@hooks/useFormatNumber'
import type { FundingLocationState } from '@hooks/useFundingNav'
import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'

import { DropdownInput } from '@shared/components/Dropdown'
import { FieldWrapper } from '@shared/components/FieldWrapper'
import { ActionModal } from '@shared/modals/Action.modal'

import { ExchangeRateModal } from '../../modals/ExchangeRateModal.modal'
import { TransferPageProps } from './WalletTransfer.page'
import { TransferFromModal } from './modals/TransferFrom.modal'
import { TransferToModal } from './modals/TransferTo.modal'

import styles from './TransferForm.module.scss'

export type WalletOrTradingAccountType = {
  id: string
  name?: string | number
  currency: NameDto<CurrencyType | undefined> | undefined
  balance: number
  platformOverview?: {
    login: string
    group: string
    equity: number
    balance: number
    lastEndOfDayBalance: number
    usedMargin: number
    freeMargin: number
    marginLevel: number
  }
  wallet?: {
    id: string
  }
  walletType?: {
    id: number
  }
  businessRules?: CampaignBusinessRuleDto[]

  campaigns?: TradingAccountCampaign[]
}

export interface TransferValues {
  transferFrom: WalletOrTradingAccountType
  transferTo: WalletOrTradingAccountType
  fromAmount: number
  minAmount: number
}

type TransferFormProps = FormikProps<TransferValues> & OuterProps

const FormField = createFormField<TransferValues>()
const FormNumericField = createFormNumericField<TransferValues>()

const TransferFormUI: React.FC<TransferFormProps> = (props) => {
  const { title, description, limits } = props
  const { values, isValid, setFieldValue, setFieldTouched } = props
  const { handleSubmit, isFromCampaign, transferFromLabel } = props
  const { transferTo, transferFrom, fromAmount } = values
  useOnLocaleSwitch(() => props.validateForm(), [values.transferTo])

  const { tradingAccountId, toTradingAccountId, tradingAccounts, wallets, walletId } = props
  const { walletTypeFrom, walletTypeTo } = props

  const exchangeRateModal = useModal(ExchangeRateModal)

  const { navigateToPreviousPath } = usePathHistoryContext()
  const navigate = useNavigate()
  const location = useLocation()
  const returnPath = (location.state as FundingLocationState | null)?.returnPath
  const { t } = useTranslation()

  const cancelModal = useModal(ActionModal, {
    onConfirm: () => {
      if (returnPath) {
        navigate(returnPath, { state: { wallets: true } })
      } else {
        navigateToPreviousPath({ state: { wallets: true } })
      }
    },
  })

  const amountOperationsProps = useAmountOperations()
  const { getReceivingAmount } = amountOperationsProps
  const { getCurrencyBalanceTo, getCurrencyBalanceFrom, walletBalance } =
    useBalanceOperations(props)

  const handleTransferFromChange = async (taOrWallet: WalletOrTradingAccountType) => {
    if (!taOrWallet) {
      return
    }

    setFieldValue('transferFrom', taOrWallet)

    if (!isWallet(taOrWallet)) {
      setFieldValue('minAmount', 0.01)
    }

    const transferTo = (() => {
      if (isWallet(taOrWallet)) {
        return
      }

      const wallet = getWallet(taOrWallet?.wallet?.id)
      if (!wallet) {
        return
      }

      return wallet.restrictions &&
        !isRestricted(WalletRestrictions.TRANSFER_TO, wallet.restrictions)
        ? wallet
        : undefined
    })()

    setFieldValue('transferTo', transferTo)
    transferFromModal.close()
  }

  const getWallet = (walletId: string | undefined) => {
    return wallets.find((wallet) => wallet.id === walletId)
  }

  const handleTransferToChange = async (taOrWallet: WalletOrTradingAccountType) => {
    await setFieldValue('transferTo', taOrWallet)
    setFieldTouched('transferTo')

    // set min amount only if transfer is from wallet to wallet
    if (values.transferFrom && values.transferFrom.walletType && isWallet(taOrWallet)) {
      const isLimitFound = limits.find((x) => x.currency.id === values.transferFrom.currency?.id)
      if (isLimitFound) {
        setFieldValue('minAmount', isLimitFound.amount)
      } else {
        setFieldValue('minAmount', 0.01)
      }
    } else {
      setFieldValue('minAmount', 0.01)
    }
    transferToModal.close()
  }

  const transferToModal = useModal(TransferToModal)

  const handleSetTransferToModalOpen = () => {
    if (transferFrom?.id && isWalletOrTa(transferFrom)) {
      transferToModal.open({
        wallets: wallets.filter((x) => walletTypeTo.includes(x.walletType.id)),
        tradingAccounts,
        transferFrom,
        transferTo,
        onTransferToChange: handleTransferToChange,
      })
    }
  }

  const transferFromModal = useModal(TransferFromModal)

  const handleTransferFromModal = () => {
    setFieldValue('fromAmount', undefined)
    transferFromModal.open({
      wallets: wallets.filter((x) => x.walletType.id === walletTypeFrom),
      tradingAccounts: tradingAccounts,
      onTransferFromChange: handleTransferFromChange,
      transferFrom: transferFrom,
      transferTo: transferTo,
    })
  }

  useEffect(() => {
    if (tradingAccountId) {
      const selectedTradingAccount = tradingAccounts?.items.find((ta) => ta.id === tradingAccountId)
      handleTransferFromChange(selectedTradingAccount as WalletOrTradingAccountType)
    } else if (walletId) {
      const selectedWallet = wallets.find((wallet) => wallet.id === walletId)
      handleTransferFromChange(selectedWallet as WalletOrTradingAccountType)
    }
    if (toTradingAccountId) {
      const selectedTradingAccount = tradingAccounts?.items.find(
        (ta) => ta.id === toTradingAccountId
      )
      handleTransferToChange(selectedTradingAccount as WalletOrTradingAccountType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useTransferExchangeRateEffect(props, amountOperationsProps)

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleSubmit()
  }

  const getTransferName = (
    isTransferFrom: boolean,
    tradingAccountOrWallet: WalletOrTradingAccountType
  ) => {
    const walletName =
      isTransferFrom && location.pathname.includes('introducing-broker')
        ? t('Wallet.Currency IB Wallet', {
            walletCurrencyId: tradingAccountOrWallet.currency?.id,
          })
        : t('Wallet.Currency Wallet', {
            walletCurrencyId: tradingAccountOrWallet.currency?.id,
          })

    return isWallet(tradingAccountOrWallet) ? walletName : tradingAccountOrWallet.name
  }

  const getTransferFromPercentageProfitRule = (businessRules?: CampaignBusinessRuleDto[]) => {
    if (!businessRules || !businessRules.length) {
      return null
    }

    const rule = businessRules.find(
      (rule) => rule.businessRule?.id === BusinessRulesEnum.TransferFromPercentageProfit
    )
    return rule ? rule.businessRuleValue : null
  }

  const transferFromPercentageProfit = getTransferFromPercentageProfitRule(
    values.transferFrom.businessRules
  )

  return (
    <FormTemplate title={title || t('Wallet.Transfer')} goBack={cancelModal.open}>
      <Form className={styles.wrapper} onSubmit={handleSubmitForm} autoComplete='off'>
        <FieldWrapper>
          {description && <p className={styles.info}>{description}</p>}
          <DropdownInput
            name={'transferFrom.id'}
            label={transferFromLabel || t('Wallet.Transfer From')}
            value={transferFrom?.id && getTransferName(true, transferFrom)}
            hint={transferFrom?.id && `${t('Wallet.Balance')} ${getCurrencyBalanceFrom()}`}
            required
            disabled={isFromCampaign}
            onClick={handleTransferFromModal}
          />
          <div>
            {transferFromPercentageProfit && (
              <p className={classNames('text', styles.marginBottom)}>
                <Trans
                  i18nKey='Wallet.You can withdraw up to of the profits'
                  values={{ value: transferFromPercentageProfit }}
                  components={[<strong />]}
                />
              </p>
            )}
            <div className={styles.amountWrapper}>
              <div className={styles.first}>
                <FormNumericField
                  name='fromAmount'
                  label={t('Wallet.Sending Amount')}
                  decimalPlaces={2}
                  disabled={!transferFrom.id}
                  required
                  step={values.minAmount}
                  autoComplete='false'
                />
              </div>
              <div className={styles.last}>
                <FormField
                  name='transferFrom.currency.name'
                  label={t('Wallet.Currency')}
                  className={styles.last}
                  value={transferFrom && transferFrom.currency?.id}
                  disabled
                />
              </div>
            </div>
          </div>
          <PercentageAmountChip
            currentAmount={fromAmount}
            walletBalance={walletBalance}
            onAmountChange={async (walletBalance) => {
              await setFieldValue('fromAmount', walletBalance.toFixed(2))
              setFieldTouched('fromAmount')
            }}
          />
          <DropdownInput
            name={'transferTo.id'}
            label={t('Wallet.Transfer To')}
            value={
              transferTo?.walletType?.id === 10
                ? transferTo?.id && getTransferName(true, transferTo)
                : transferTo?.id && getTransferName(false, transferTo)
            }
            hint={transferTo?.id && `${t('Wallet.Balance')} ${getCurrencyBalanceTo()}`}
            disabled={!transferFrom?.id || isFromCampaign}
            required
            onClick={handleSetTransferToModalOpen}
          />
          {transferFrom?.id &&
            transferTo?.id &&
            transferFrom?.currency?.id !== transferTo?.currency?.id && (
              <div>
                <div className={styles.amountWrapper}>
                  <div className={styles.first}>
                    <FormField
                      name='transferTo.currency.name'
                      label={t('Wallet.Receiving amount')}
                      autoComplete='false'
                      min={0}
                      onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                      value={getReceivingAmount(fromAmount)}
                      required
                      disabled
                    />
                  </div>
                  <div className={styles.last}>
                    <FormField
                      name='transferTo.currency.id'
                      label={t('Currency')}
                      value={transferTo?.currency?.id}
                      disabled
                      className={styles.last}
                    />
                  </div>
                </div>

                {transferFrom?.currency?.id && transferTo?.currency?.id && (
                  <div className='mt-4'>
                    <ExchangeRateBox
                      currencyFrom={transferFrom.currency.id}
                      currencyTo={transferTo.currency.id}
                      calculatedAmount={getReceivingAmount(1, 5)}
                      infoIconClick={() => exchangeRateModal.open()}
                    />
                  </div>
                )}
              </div>
            )}
          <div className={styles.action}>
            <Button.Secondary size='lg' fullWidth onClick={() => cancelModal.open()}>
              {t('Cancel')}
            </Button.Secondary>
            <Button.Primary disabled={!isValid} type='submit' fullWidth size='lg'>
              {t('Confirm')}
            </Button.Primary>
          </div>
        </FieldWrapper>
      </Form>
    </FormTemplate>
  )
}

const useTransferExchangeRateEffect = (
  props: TransferFormProps,
  amountOperationsProps: ReturnType<typeof useAmountOperations>
) => {
  const { values } = props
  const { transferTo, transferFrom } = values

  const apiClient = useApiClient(ClientApiClient)

  useEffect(() => {
    ;(async () => {
      if (
        transferFrom?.id &&
        transferTo?.id &&
        transferFrom?.currency?.id &&
        transferTo?.currency?.id &&
        transferFrom?.currency?.id !== transferTo?.currency?.id
      ) {
        const { rate } = await apiClient.getExchangeRate({
          BaseCcy: transferFrom.currency.id,
          QuoteCcy: transferTo.currency.id,
          direction: 'sell',
          BaseWalletTypeId: transferFrom.walletType?.id,
          QuoteWalletTypeId: transferTo.walletType?.id,
        })
        amountOperationsProps.setExchangeRate(rate)
      } else {
        amountOperationsProps.setExchangeRate(0)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferFrom, transferTo])
}

export const isWalletOrTa = (walletOrTa: WalletOrTradingAccountType): boolean => {
  return isWallet(walletOrTa) || isTradingAccount(walletOrTa)
}

export const isWallet = (wallet: WalletOrTradingAccountType): boolean => {
  return 'walletType' in wallet || 'walletState' in wallet
}

export const isTradingAccount = (ta: WalletOrTradingAccountType): boolean => {
  return 'platformType' in ta || 'leverage' in ta
}

interface OuterProps {
  limits: TransferPageProps['limits']
  title: string
  entity: TickmillCompaniesEnum
  description?: React.ReactNode
  wallets: WalletDto[]
  tradingAccounts: PageData<TradingAccount> | undefined
  walletId?: string
  walletTypeFrom: WalletTypeEnum
  walletTypeTo: WalletTypeEnum[]
  tradingAccountId?: string
  toTradingAccountId?: string
  handleFormSubmit: (values: TransferValues) => void
  isFromCampaign?: boolean
  transferFromLabel?: string
}

export const TransferForm = withFormik<OuterProps, TransferValues>({
  mapPropsToValues: () => {
    const minAmount = 0.01
    return {
      transferFrom: {
        id: '',
        currency: undefined,
        balance: 0.0,
        wallet: undefined,
        platformOverview: undefined,
      },
      transferTo: {
        id: '',
        currency: undefined,
        balance: 0.0,
        wallet: undefined,
        platformOverview: undefined,
      },
      fromAmount: 0.0,
      minAmount,
    }
  },
  handleSubmit: async (values, { props }) => {
    props.handleFormSubmit(values)
  },
  validate: (values, { walletTypeFrom }) => {
    const errors: FormikErrors<TransferValues> = {}

    if (!values.transferFrom.id) {
      errors.transferFrom = {
        id: t('Validation.Required'),
      }
    }

    if (values.fromAmount <= 0) {
      errors.fromAmount = t('Validation.Amount should be greater than 0.00')
    }

    if (isValidMinAmount(values)) {
      errors.fromAmount = t('Validation.Minimum amount is {{amount}} {{currency}}', {
        currency: values.transferFrom.currency?.id,
        amount: values.minAmount,
      })
    }

    if (isValidMaxAmount(values)) {
      if (isWallet(values?.transferFrom)) {
        errors.fromAmount = t('Validation.There is not enough balance in your wallet')
      }

      if (!isWallet(values?.transferFrom)) {
        errors.fromAmount = t('Validation.There is not enough balance in your trading account')
      }
    }

    if (walletTypeFrom === WalletTypeEnum.IB && (values.fromAmount < 50 || !values.fromAmount)) {
      errors.fromAmount = t(`Validation.Amount cannot be below allowed minimum of`, {
        amount: `50.00 ${values.transferFrom.currency?.id || 'USD'}`,
      })
    }

    if (!values?.transferTo?.id) {
      errors.transferTo = {
        id: t('Validation.Required'),
      }
    }

    return errors
  },
  enableReinitialize: true,
  validateOnMount: true,
})(TransferFormUI)

const isValidMinAmount = (values: TransferValues): boolean => {
  const minAmount = values?.minAmount
  const amount = values?.fromAmount
  return amount < minAmount
}

const isValidMaxAmount = (values: TransferValues): boolean => {
  const maxAmount = getBalance(values?.transferFrom) ?? 0
  const amount = values?.fromAmount
  return amount > maxAmount
}

const useAmountOperations = () => {
  const { formatNumber } = useFormatNumber()

  const [exchangeRate, setExchangeRate] = useState<number>(0)

  const getReceivingAmount = (fromAmount: number, maxDecimalPlaces = 2) => {
    if (!fromAmount) return '-'
    const amount = exchangeRate > 0 ? fromAmount * exchangeRate : fromAmount
    return formatNumber(amount, { maxDecimalPlaces })
  }

  return {
    setExchangeRate,
    getBalance,
    getReceivingAmount,
  }
}

const useBalanceOperations = (props: TransferFormProps) => {
  const { values } = props

  const { formatMoney } = useFormatNumber()

  const getCurrencyBalanceFrom = () => {
    const transferFrom = values?.transferFrom
    return formatMoney(getBalance(transferFrom), transferFrom?.currency?.id)
  }

  const getCurrencyBalanceTo = () => {
    const transferTo = values?.transferTo
    return formatMoney(getBalance(transferTo), transferTo?.currency?.id)
  }

  const getBalance = (walletOrTa: WalletOrTradingAccountType) => {
    return isWallet(walletOrTa) ? walletOrTa.balance : walletOrTa.platformOverview?.balance
  }

  const walletBalance = getBalance(values.transferFrom)

  return {
    walletBalance,
    getBalance,
    getCurrencyBalanceFrom,
    getCurrencyBalanceTo,
  }
}

const getBalance = (walletOrTa: WalletOrTradingAccountType) => {
  return isWallet(walletOrTa) ? walletOrTa.balance : walletOrTa.platformOverview?.balance
}
